<template>
  <v-layout>
    <div class="padding-container-sm">
      <v-page-header>
        <div slot="header">
          <div class="row">
            <div class="col">
              <b-input-group size="md">
                <b-input-group-text
                  slot="append"
                  class="bg-white"
                >
                  <strong><i class="icon ion-md-search" /></strong>
                </b-input-group-text>
                <b-form-input
                  class="border-right-0"
                  :value="filter.searchValue"
                  placeholder="Search for author/navigator name or ID"
                  @keyup.enter.native="onSearchText"
                  @blur.native="onSearchText"
                />
              </b-input-group>
            </div>
          </div>
        </div>
        <div
          slot="action"
          v-if="isNotEmpty(userCount)"
        >
          <span
            v-for="(statusCount,index) in userCount"
            :key="index"
            class="status-count-content"
            :class="{active : statusCount.designation === filter.designation}"
            @click="setStatus(statusCount.designation)"
          >
            <span
              class="btn btn-link"
              :class="{disabled : statusCount.designation === filter.designation,
                       [statusCount.designation]: true}"
            >{{ statusCount.designation === 'FIRST_RESPONDER' ? 'Initial Call Receiver' : statusCount.designation | normalizetext}}
              <!-- <span v-if="index === userCount.length -1">All</span> -->
            </span>
            <span class="count">({{ statusCount.count }})</span>
          </span>
        </div>
      </v-page-header>
      <v-table
        :options="tableProps"
        :total-count="totalCount"
        :data="users"
      >
        <template
          slot="name"
          slot-scope="slotProps"
        >
          <div class="align-items-center d-flex patient-name-content">
            <img
              :src="require(`@/assets/image/${slotProps.data.datum.gender === 'male'
              ? 'boy': 'girl'}.svg`)"
                width="35"
                height="35"
                :alt="slotProps.data.datum.first_name"
                v-b-tooltip.hover
                :title="slotProps.data.datum.first_name"
               >
            <div
              class="d-flex flex-column justify-content-center"
              v-if="slotProps"
            >
              <span
                class="btn btn-link text-truncate text-left pl-0 pr-0"
                @click="viewUser(slotProps.data.datum.id)"
                v-b-tooltip.hover
                :title="slotProps.data.datum.first_name"
              >
                {{ slotProps.data.datum.first_name | capitalizefirst }}
                {{ slotProps.data.datum.last_name | capitalizefirst }}
              </span>
            </div>
          </div>
        </template>
        <template
          slot="designation"
          slot-scope="slotProps"
        >
        <span v-if="slotProps.data.datum.designation === 'CARE_AUTHOR'">
            Care Author
        </span>
         <span v-if="slotProps.data.datum.designation === 'CARE_NAVIGATOR'">
            Care Navigator
        </span>
         <span v-if="slotProps.data.datum.designation === 'FIRST_RESPONDER'">
            Initial Call Receiver
        </span>
        </template>
        <template
          slot="mobileNumber"
          slot-scope="slotProps"
        >
          {{ slotProps.data.datum.mobile_no | mobileformat(slotProps.data.datum.country_code) }}
        </template>
        <template
          slot="actions"
          slot-scope="slotProps"
        >
          <span
            v-if="slotProps"
            class="d-flex justify-content-around"
          >
            <button
              class="btn-modify"
              v-if="permissions.user.edit"
              @click="editUser(slotProps.data.datum.id)"
            >
              <i class="icon ion-md-create" />Modify
            </button>
            <button
              class="btn-delete"
              v-if="permissions.user.delete"
              @click="deleteUser(slotProps.data.datum)"
            >
              <i class="icon ion-md-close" />Delete
            </button>
          </span>
        </template>
      </v-table>
    </div>
    <v-loader :isLoading="isLoading" />
  </v-layout>
</template>

<script>
import VTable from '@/components/table/index.vue';
import { mapGetters } from 'vuex';
import VLayout from '@/layouts/Default.vue';
import VPageHeader from '@/components/pageheader/PageHeader.vue';
import VLoader from '@/components/loader/index.vue';

export default {
  name: 'Users',
  components: {
    VTable,
    VLayout,
    VPageHeader,
    VLoader
  },
  data() {
    return {
      filter: {
        status: 'All',
        searchValue: '',
        limit: 10,
        pageNo: 1
      },
      roles: {
        CARE_AUTHOR: 'Author',
        CARE_NAVIGATOR: 'Navigator'
      },
      tableProps: {
        columns: [
          {
            headerCellProps: {
              class: 'header-cell'
            },
            cellProps: {
              style: { maxWidth: '150px' },
              class: 'text-truncate text-left'
            },
            cellSlot: {
              name: 'name'
            },
            dataKey: 'first_name',
            dataType: String,
            label: 'Users'
          },
          {
            headerCellProps: {
              class: 'header-cell'
            },
            cellSlot: {
              name: 'designation'
            },
            cellProps: {},
            dataKey: 'designation',
            /**
             * used to format values that needs filtering
             * ref=>https://github.com/vuejs/Discussion/issues/236
             * */
            dataFilter: 'normalizetext',
            dataType: String,
            label: 'Roles'
          },
          {
            headerCellProps: {
              class: 'header-cell'
            },
            cellSlot: {
              name: 'mobileNumber'
            },
            cellProps: {},
            dataKey: 'mobile_no',
            dataType: String,
            label: 'Mobile Number'
          },
          {
            headerCellProps: {
              class: 'header-cell'
            },
            cellProps: {},
            dataKey: 'email',
            dataType: String,
            label: 'Email Address'
          },
          {
            disabled: this.enableActions,
            headerCellProps: {
              class: 'header-cell'
            },
            cellProps: {},
            cellSlot: {
              name: 'actions'
            },
            disablePadding: false,
            label: 'Actions'
          }
        ],
        enableSelect: false,
        onPageChange: this.onPageChange,
        page: 1,
        rowProps: {},
        rowsPerPage: 10,
        title: 'All users'
      }
    };
  },
  computed: {
    ...mapGetters({
      users: 'user/getAllUsers',
      userCount: 'user/getUserCount',
      totalCount: 'user/getTotalCount',
      permissions: 'auth/getModulePermissions',
      isLoading: 'user/getIsUsersloading'
    }),
    enableActions() {
      return this.permissions.user.edit;
    }
  },
  watch: {
    filter: {
      handler: 'fetchUsers',
      immediate: true
    }
  },
  methods: {
    onPageChange(pageNo, rowsPerPage) {
      this.tableProps.page = pageNo;
      this.filter = {
        ...this.filter,
        pageNo,
        limit: rowsPerPage
      };
    },
    onSearchText(event) {
      this.filter = {
        ...this.filter,
        searchValue: event.target.value
      };
    },
    fetchUsers() {
      this.$store.dispatch('user/viewAll', this.filter);
    },
    deleteUser(user) {
      const content = {
        title: 'Delete User',
        message: `Do you want to delete this user ${user.first_name} ${user.last_name}?`
      };
      this.conFirmAlert(
        content,
        () => {
          // confirm
          const data = { is_active: false };
          this.$store.dispatch('user/remove', { ...data, id: user.id });
        },
        () => {
          // cancel
        }
      );
    },
    setStatus(currentStatus) {
      if (this.filter.status !== currentStatus) {
        this.filter = {
          ...this.filter,
          status: currentStatus
        };
      }
    },
    viewUser(id) {
      this.$router.push({
        name: 'user.view', params: { id }
      });
    },
    editUser(id) {
      this.$router.push({
        name: 'user.edit', params: { id }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "./style.scss";
</style>
